import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '../context/ThemeContext';

// Country name to ISO code mapping
const COUNTRY_TO_ISO_MAP = {
  'Argentina': 'ARG',
  'Australia': 'AUS',
  'Austria': 'AUT',
  'Bangladesh': 'BGD',
  'Belgium': 'BEL',
  'Bolivia': 'BOL',
  'Brazil': 'BRA',
  'Canada': 'CAN',
  'Chile': 'CHL',
  'China': 'CHN',
  'Egypt': 'EGY',
  'Finland': 'FIN',
  'France': 'FRA',
  'Germany': 'DEU',
  'India': 'IND',
  'Italy': 'ITA',
  'Japan': 'JPN',
  'Malaysia': 'MYS',
  'Poland': 'POL',
  'Rep. of Korea': 'KOR',
  'Russian Federation': 'RUS',
  'Saudi Arabia': 'SAU',
  'Singapore': 'SGP',
  'Slovenia': 'SVN',
  'South Africa': 'ZAF',
  'United Arab Emirates': 'ARE',
  'United Kingdom': 'GBR',
  'United States': 'USA',
  'USA': 'USA',
  'Viet Nam': 'VNM',
  'Vietnam': 'VNM',
  // Add more mappings as needed
  'UK': 'GBR',
  'US': 'USA',
  'Korea': 'KOR',
  'South Korea': 'KOR',
  'UAE': 'ARE',
  'Russia': 'RUS'
};

// Function to get ISO code from country name
const getCountryISOCode = (countryName) => {
  return COUNTRY_TO_ISO_MAP[countryName] || countryName.toUpperCase().slice(0, 3);
};

// Dynamic Nivo theme that adapts to light/dark mode
const getNivoTheme = (isDark = true) => ({
  background: 'transparent',
  text: {
    fontSize: 11,
    fill: isDark ? '#e5e7eb' : '#374151',
    outlineWidth: 0,
    outlineColor: 'transparent',
  },
  axis: {
    domain: {
      line: {
        stroke: isDark ? '#374151' : '#d1d5db',
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 12,
        fill: isDark ? '#9ca3af' : '#6b7280',
      },
    },
    ticks: {
      line: {
        stroke: isDark ? '#374151' : '#d1d5db',
        strokeWidth: 1,
      },
      text: {
        fontSize: 11,
        fill: isDark ? '#e5e7eb' : '#374151',
      },
    },
  },
  grid: {
    line: {
      stroke: isDark ? '#374151' : '#d1d5db',
      strokeWidth: 1,
    },
  },
  legends: {
    title: {
      text: {
        fontSize: 11,
        fill: isDark ? '#e5e7eb' : '#374151',
      },
    },
    text: {
      fontSize: 11,
      fill: isDark ? '#e5e7eb' : '#374151',
    },
    ticks: {
      line: {},
      text: {
        fontSize: 10,
        fill: isDark ? '#e5e7eb' : '#374151',
      },
    },
  },
  annotations: {
    text: {
      fontSize: 13,
      fill: isDark ? '#e5e7eb' : '#374151',
      outlineWidth: 2,
      outlineColor: isDark ? '#ffffff' : '#000000',
      outlineOpacity: 1,
    },
    link: {
      stroke: isDark ? '#ffffff' : '#000000',
      strokeWidth: 1,
      outlineWidth: 2,
      outlineColor: isDark ? '#ffffff' : '#000000',
      outlineOpacity: 1,
    },
    outline: {
      stroke: isDark ? '#ffffff' : '#000000',
      strokeWidth: 2,
      outlineWidth: 2,
      outlineColor: isDark ? '#ffffff' : '#000000',
      outlineOpacity: 1,
    },
    symbol: {
      fill: isDark ? '#ffffff' : '#000000',
      outlineWidth: 2,
      outlineColor: isDark ? '#ffffff' : '#000000',
      outlineOpacity: 1,
    },
  },
  tooltip: {
    container: {
      background: isDark ? '#1f2937' : '#ffffff',
      color: isDark ? '#e5e7eb' : '#374151',
      fontSize: 12,
      border: isDark ? '1px solid #374151' : '1px solid #d1d5db',
      borderRadius: '4px',
    },
    basic: {},
    chip: {},
    table: {},
    tableCell: {},
    tableCellValue: {},
  },
});

const TimeSeriesCharts = ({ 
  data, 
  loading,
  selectedMineral, 
  selectedFlow, 
  selectedYear,
  mineralColors, 
  mineralDarkColors 
}) => {
  const { isDarkMode } = useTheme();
  const nivoTheme = getNivoTheme(isDarkMode);
  
  // Helper function to validate date strings (handles YYYY-MM format)
  const isValidDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string' || dateString.trim() === '') return false;
    try {
      // Handle YYYY-MM format by appending '-01' for day
      const testDate = dateString.includes('-') && dateString.split('-').length === 2 
        ? new Date(dateString + '-01')
        : new Date(dateString);
      
      if (isNaN(testDate.getTime())) return false;
      
      // Additional validation for YYYY-MM format
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = dateString.split('-').map(Number);
        if (year < 1900 || year > 2100 || month < 1 || month > 12) return false;
      }
      
      return true;
    } catch (e) {
      return false;
    }
  };

  // Deep clean function to ensure all data points are valid before passing to Nivo
  const deepCleanDataForNivo = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    
    return dataArray
      .map(series => {
        if (!series || !series.data || !Array.isArray(series.data)) return null;
        
        const cleanedData = series.data
          .filter(point => {
            // Ensure point exists and has required properties
            if (!point || typeof point !== 'object') return false;
            if (point.x === null || point.x === undefined) return false;
            if (point.y === null || point.y === undefined || isNaN(point.y)) return false;
            
            // Validate x is a string and is a valid date format
            if (typeof point.x !== 'string' || point.x.trim() === '') return false;
            if (!isValidDate(point.x)) return false;
            
            return true;
          })
          .map(point => ({
            x: String(point.x).trim(), // Ensure x is a clean string
            y: Number(point.y) || 0    // Ensure y is a number
          }));
        
        // Only return series if it has valid data points
        if (cleanedData.length === 0) return null;
        
        return {
          ...series,
          data: cleanedData
        };
      })
      .filter(series => series !== null && series.data && series.data.length > 0);
  };
  
  // Data transformation functions for Nivo charts
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${parseFloat((value / 1000000).toFixed(2))}M`;
    } else if (value >= 1000) {
      return `$${parseFloat((value / 1000).toFixed(2))}K`;
        } else {
      return `$${parseFloat(value.toFixed(2))}`;
    }
  };

  // Format function for growth data (handles negative values better)
  const formatGrowthCurrency = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
      return `${value < 0 ? '-' : ''}$${parseFloat((absValue / 1000000).toFixed(2))}M`;
    } else if (absValue >= 1000) {
      return `${value < 0 ? '-' : ''}$${parseFloat((absValue / 1000).toFixed(2))}K`;
    } else {
      return `${value < 0 ? '-' : ''}$${parseFloat(absValue.toFixed(2))}`;
    }
  };

  // Format month-year for tooltips (e.g., "2024-01" -> "Jan 2024")
  const formatMonthYear = (monthYear) => {
    if (!monthYear) return monthYear;
    try {
      const [year, month] = monthYear.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch (error) {
      return monthYear; // Return original if parsing fails
    }
  };

  // Map country names to ISO codes
  const getCountryISO = (countryName) => {
    const countryMap = {
      'United States': 'USA',
      'USA': 'USA',
      'China': 'CHN',
      'United Arab Emirates': 'ARE',
      'UAE': 'ARE',
      'Chile': 'CHL',
      'Viet Nam': 'VNM',
      'Vietnam': 'VNM',
      'Russian Federation': 'RUS',
      'Russia': 'RUS',
      'Norway': 'NOR',
      'Japan': 'JPN',
      'Germany': 'DEU',
      'Australia': 'AUS',
      'Canada': 'CAN',
      'Brazil': 'BRA',
      'India': 'IND',
      'South Korea': 'KOR',
      'Korea': 'KOR',
      'United Kingdom': 'GBR',
      'UK': 'GBR',
      'France': 'FRA',
      'Italy': 'ITA',
      'Spain': 'ESP',
      'Netherlands': 'NLD',
      'Belgium': 'BEL',
      'Switzerland': 'CHE',
      'Austria': 'AUT',
      'Sweden': 'SWE',
      'Denmark': 'DNK',
      'Finland': 'FIN',
      'Poland': 'POL',
      'Czech Republic': 'CZE',
      'Hungary': 'HUN',
      'Romania': 'ROU',
      'Bulgaria': 'BGR',
      'Greece': 'GRC',
      'Portugal': 'PRT',
      'Ireland': 'IRL',
      'Luxembourg': 'LUX',
      'Malta': 'MLT',
      'Cyprus': 'CYP',
      'Estonia': 'EST',
      'Latvia': 'LVA',
      'Lithuania': 'LTU',
      'Slovenia': 'SVN',
      'Slovakia': 'SVK',
      'Croatia': 'HRV'
    };
    return countryMap[countryName] || countryName;
  };

  // Map ISO codes back to full country names for tooltips
  const getCountryName = (isoCode) => {
    const isoToNameMap = {
      'USA': 'United States',
      'CHN': 'China',
      'ARE': 'United Arab Emirates',
      'CHL': 'Chile',
      'VNM': 'Viet Nam',
      'RUS': 'Russian Federation',
      'NOR': 'Norway',
      'JPN': 'Japan',
      'DEU': 'Germany',
      'AUS': 'Australia',
      'CAN': 'Canada',
      'BRA': 'Brazil',
      'IND': 'India',
      'KOR': 'South Korea',
      'GBR': 'United Kingdom',
      'FRA': 'France',
      'ITA': 'Italy',
      'ESP': 'Spain',
      'NLD': 'Netherlands',
      'BEL': 'Belgium',
      'CHE': 'Switzerland',
      'AUT': 'Austria',
      'SWE': 'Sweden',
      'DNK': 'Denmark',
      'FIN': 'Finland',
      'POL': 'Poland',
      'CZE': 'Czech Republic',
      'HUN': 'Hungary',
      'ROU': 'Romania',
      'BGR': 'Bulgaria',
      'GRC': 'Greece',
      'PRT': 'Portugal',
      'IRL': 'Ireland',
      'LUX': 'Luxembourg',
      'MLT': 'Malta',
      'CYP': 'Cyprus',
      'EST': 'Estonia',
      'LVA': 'Latvia',
      'LTU': 'Lithuania',
      'SVN': 'Slovenia',
      'SVK': 'Slovakia',
      'HRV': 'Croatia'
    };
    return isoToNameMap[isoCode] || isoCode;
  };

  // Transform 3-year summary data for Nivo bar chart
  const threeYearBarData = useMemo(() => {
    if (!data || !data.threeYearSummary || !Array.isArray(data.threeYearSummary)) return [];
    
    return data.threeYearSummary
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 8)
      .map(item => ({
        country: getCountryISOCode(item.country),
        value: item.totalValue,
        fullName: item.country,
      }));
  }, [data?.threeYearSummary]);

  // Transform 5-year summary data for Nivo bar chart
  const fiveYearBarData = useMemo(() => {
    if (!data || !data.fiveYearSummary || !Array.isArray(data.fiveYearSummary)) return [];
    
    return data.fiveYearSummary
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 8)
      .map(item => ({
        country: getCountryISOCode(item.country),
        value: item.totalValue,
        fullName: item.country,
      }));
  }, [data?.fiveYearSummary]);

  // Transform moving averages data for Nivo line chart
  const movingAveragesLineData = useMemo(() => {
    if (!data || !data.movingAverages || !data.movingAverages.months || !Array.isArray(data.movingAverages.months)) return [];
    
    const months = data.movingAverages.months;
    const rolling3M = data.movingAverages.rolling3M || [];
    const rolling6M = data.movingAverages.rolling6M || [];
    const rolling12M = data.movingAverages.rolling12M || [];
    
    // Helper to safely create data points
    const createDataPoint = (month, value) => {
      if (!month || typeof month !== 'string' || !isValidDate(month)) return null;
      const numValue = value != null && !isNaN(value) ? Number(value) : 0;
      return {
        x: month,
        y: numValue,
      };
    };
    
    // Filter data based on selected year and validate dates - be very strict
    const filteredData = months
      .map((month, index) => ({
        month: month && typeof month === 'string' ? month.trim() : null,
        rolling3M: rolling3M[index],
        rolling6M: rolling6M[index],
        rolling12M: rolling12M[index],
        index
      }))
      .filter(item => {
        // Validate that month is a valid date string
        if (!item.month || !isValidDate(item.month)) return false;
        if (selectedYear === 'All') return true;
        try {
          const itemYear = item.month.split('-')[0];
          return itemYear === selectedYear;
        } catch (e) {
          return false;
        }
      });
    
    const rolling3MData = filteredData
      .map(item => createDataPoint(item.month, item.rolling3M))
      .filter(point => point !== null);
    
    const rolling6MData = filteredData
      .map(item => createDataPoint(item.month, item.rolling6M))
      .filter(point => point !== null);
    
    const rolling12MData = filteredData
      .map(item => createDataPoint(item.month, item.rolling12M))
      .filter(point => point !== null);
    
    return [
      {
        id: '3-Month',
        color: '#10b981',
        data: rolling3MData,
      },
      {
        id: '6-Month', 
        color: '#f59e0b',
        data: rolling6MData,
      },
      {
        id: '12-Month',
        color: '#ef4444',
        data: rolling12MData,
      },
    ].filter(series => series.data && series.data.length > 0);
  }, [data?.movingAverages, selectedYear]);

  // Transform growth analysis data for Nivo grouped bar chart
  const growthAnalysisBarData = useMemo(() => {
    if (!data || !data.growthAnalysis || typeof data.growthAnalysis !== 'object') return [];
    
    const getYearRangesForGrowth = (selectedYear) => {
      if (selectedYear === 'All') {
        const currentYear = new Date().getFullYear();
        return {
          threeYear: { current: `${currentYear - 2}-${currentYear}`, previous: `${currentYear - 5}-${currentYear - 3}` },
          fiveYear: { current: `${currentYear - 4}-${currentYear}`, previous: `${currentYear - 9}-${currentYear - 5}` }
        };
      }
      
      const year = parseInt(selectedYear);
      return {
        threeYear: { current: `${year - 2}-${year}`, previous: `${year - 5}-${year - 3}` },
        fiveYear: { current: `${year - 4}-${year}`, previous: `${year - 9}-${year - 5}` }
      };
    };

    const yearRanges = getYearRangesForGrowth(selectedYear);

    return [
      { 
        period: "3-Year", 
        current: data.growthAnalysis?.threeYear?.current || 0, 
        previous: data.growthAnalysis?.threeYear?.previous || 0,
        currentLabel: yearRanges.threeYear.current,
        previousLabel: yearRanges.threeYear.previous
      },
      { 
        period: "5-Year", 
        current: data.growthAnalysis?.fiveYear?.current || 0, 
        previous: data.growthAnalysis?.fiveYear?.previous || 0,
        currentLabel: yearRanges.fiveYear.current,
        previousLabel: yearRanges.fiveYear.previous
      }
    ];
  }, [data?.growthAnalysis, selectedYear]);

  // Transform country data for Nivo stacked bar chart
  const countryStackedBarData = useMemo(() => {
    if (!data || !data.countryData || !Array.isArray(data.countryData)) return { data: [], keys: [] };
    
    // Filter data based on selected year first and validate dates - be very strict
    const filteredCountryData = data.countryData.filter(d => {
      if (!d || !d.month || typeof d.month !== 'string' || d.month.trim() === '' || !isValidDate(d.month)) return false;
      if (selectedYear === 'All') return true;
      try {
        const itemYear = d.month.split('-')[0];
        return itemYear === selectedYear;
      } catch (e) {
        return false;
      }
    });
    
    // Get all unique countries that appear in the filtered data
    const allCountries = new Set();
    filteredCountryData.forEach(d => {
      Object.keys(d).forEach(key => {
        if (key !== 'month' && key !== 'other' && key !== 'date' && d[key] > 0) {
          allCountries.add(key);
        }
      });
    });
    
    // Calculate total values for each country across filtered months
    const countryTotals = Array.from(allCountries).map(country => {
      const total = filteredCountryData.reduce((sum, d) => sum + (d[country] || 0), 0);
      return { country, total };
    });
    
    // Sort by total value and take top 3
    const top3Countries = countryTotals
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
    
    // Create ISO code mapping for the top 3 countries
    const countryISOMap = {};
    const isoKeys = [];
    top3Countries.forEach((item, index) => {
      const isoCode = getCountryISO(item.country);
      countryISOMap[item.country] = isoCode;
      isoKeys.push(isoCode);
    });
    
    // Transform filtered data for Nivo stacked bars - ensure all values are valid
    const transformedData = filteredCountryData
      .filter(d => d && d.month && isValidDate(d.month))
      .map(d => {
        const barItem = { month: d.month };
        top3Countries.forEach(({ country }) => {
          const isoCode = countryISOMap[country];
          const value = d[country];
          barItem[isoCode] = value != null && !isNaN(value) ? Number(value) : 0;
        });
        return barItem;
      })
      .filter(item => item && item.month && isValidDate(item.month));
    
    // Return data with ISO codes as keys, in reverse order (top country first)
    return { 
      data: transformedData, 
      keys: isoKeys.reverse() // Reverse to show top country first in legend
    };
  }, [data?.countryData, selectedYear]);

  // Transform YoY growth data for Nivo line chart
  const yoyGrowthLineData = useMemo(() => {
    if (!data || !data.monthlyData || !Array.isArray(data.monthlyData)) {
      return {
        percentageData: [],
        usdData: []
      };
    }
    
    // Get the latest year's data and previous year's data
    const currentYear = selectedYear === 'All' ? new Date().getFullYear() : parseInt(selectedYear);
    const previousYear = currentYear - 1;
    
    // Filter monthly data for current and previous years with date validation
    const currentYearData = data.monthlyData
      .filter(d => d.month && isValidDate(d.month))
      .filter(d => {
        try {
          const itemYear = parseInt(d.month.split('-')[0]);
          return itemYear === currentYear;
        } catch (e) {
          return false;
        }
      })
      .sort((a, b) => a.month.localeCompare(b.month));
    
    const previousYearData = data.monthlyData
      .filter(d => d.month && isValidDate(d.month))
      .filter(d => {
        try {
          const itemYear = parseInt(d.month.split('-')[0]);
          return itemYear === previousYear;
        } catch (e) {
          return false;
        }
      })
      .sort((a, b) => a.month.localeCompare(b.month));
    
    // Calculate month-over-month growth rate for current year
    // Sort by month to ensure proper order
    const sortedCurrentYearData = [...currentYearData].sort((a, b) => {
      const aMonth = parseInt(a.month.split('-')[1]);
      const bMonth = parseInt(b.month.split('-')[1]);
      return aMonth - bMonth;
    });
    
    const currentYearMonthlyGrowth = sortedCurrentYearData.map((currentMonth, index) => {
      if (index === 0) {
        // First month has no previous month, so growth is 0 or undefined
        return null;
      }
      
      const previousMonth = sortedCurrentYearData[index - 1];
      const currentValue = currentMonth.value || 0;
      const previousValue = previousMonth.value || 0;
      
      // Calculate month-over-month growth rate
      if (previousValue === 0 || previousValue === null) {
        return null; // Cannot calculate growth if previous value is 0
      }
      
      const growthRate = ((currentValue - previousValue) / previousValue) * 100;
      
      // Format month label with year to distinguish between years
      const monthNum = parseInt(currentMonth.month.split('-')[1]);
      const year = parseInt(currentMonth.month.split('-')[0]);
      const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = `${monthLabels[monthNum - 1] || currentMonth.month} ${year.toString().slice(-2)}`;
      
      return {
        x: monthLabel,
        y: growthRate
      };
    }).filter(item => item !== null);
    
    // Calculate month-over-month growth rate for previous year
    const sortedPreviousYearData = [...previousYearData].sort((a, b) => {
      const aMonth = parseInt(a.month.split('-')[1]);
      const bMonth = parseInt(b.month.split('-')[1]);
      return aMonth - bMonth;
    });
    
    const previousYearMonthlyGrowth = sortedPreviousYearData.map((currentMonth, index) => {
      if (index === 0) {
        // First month has no previous month, so growth is 0 or undefined
        return null;
      }
      
      const previousMonth = sortedPreviousYearData[index - 1];
      const currentValue = currentMonth.value || 0;
      const previousValue = previousMonth.value || 0;
      
      // Calculate month-over-month growth rate
      if (previousValue === 0 || previousValue === null) {
        return null; // Cannot calculate growth if previous value is 0
      }
      
      const growthRate = ((currentValue - previousValue) / previousValue) * 100;
      
      // Format month label with year to distinguish between years
      const monthNum = parseInt(currentMonth.month.split('-')[1]);
      const year = parseInt(currentMonth.month.split('-')[0]);
      const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = `${monthLabels[monthNum - 1] || currentMonth.month} ${year.toString().slice(-2)}`;
      
      return {
        x: monthLabel,
        y: growthRate
      };
    }).filter(item => item !== null);
    
    // For backward compatibility, also create percentage distribution data
    // (in case it's used elsewhere, but we'll use monthly growth for the chart)
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const currentYearPercentageData = monthLabels.map((month, index) => {
      const currentMonthData = currentYearData.find(d => {
        const monthNum = d.month.split('-')[1];
        return parseInt(monthNum) === index + 1;
      });
      
      // Calculate percentage relative to the total for that year
      const currentValue = currentMonthData ? currentMonthData.value : 0;
      const currentYearTotal = currentYearData.reduce((sum, d) => sum + (d.value || 0), 0);
      const currentPercentage = currentYearTotal > 0 ? (currentValue / currentYearTotal) * 100 : 0;
      
      return {
        x: month,
        y: currentPercentage
      };
    });
    
    const previousYearPercentageData = monthLabels.map((month, index) => {
      const previousMonthData = previousYearData.find(d => {
        const monthNum = d.month.split('-')[1];
        return parseInt(monthNum) === index + 1;
      });
      
      // Calculate percentage relative to the total for that year
      const previousValue = previousMonthData ? previousMonthData.value : 0;
      const previousYearTotal = previousYearData.reduce((sum, d) => sum + (d.value || 0), 0);
      const previousPercentage = previousYearTotal > 0 ? (previousValue / previousYearTotal) * 100 : 0;
      
      return {
        x: month,
        y: previousPercentage
      };
    });
    
    // Calculate year-over-year growth by comparing annual totals
    // Formula: YoY Growth (%) = ((Year_t - Year_(t-1)) / Year_(t-1)) × 100
    // This compares each year's total to the previous year's total
    const years = [2020, 2021, 2022, 2023, 2024];
    
    const yoyGrowthDataPoints = years.map((year) => {
      // Get data for current year
      const currentYearData = data.monthlyData
        .filter(d => d.month && isValidDate(d.month))
        .filter(d => {
          try {
            const itemYear = parseInt(d.month.split('-')[0]);
            return itemYear === year;
          } catch (e) {
            return false;
          }
        });
      
      // Get data for previous year
      const previousYear = year - 1;
      const previousYearData = data.monthlyData
        .filter(d => d.month && isValidDate(d.month))
        .filter(d => {
          try {
            const itemYear = parseInt(d.month.split('-')[0]);
            return itemYear === previousYear;
          } catch (e) {
            return false;
          }
        });
      
      // Calculate totals
      const currentYearTotal = currentYearData.reduce((sum, d) => sum + (d.value || 0), 0);
      const previousYearTotal = previousYearData.reduce((sum, d) => sum + (d.value || 0), 0);
      
      // Calculate year-over-year growth percentage
      if (previousYearTotal === 0 || previousYearTotal === null || previousYearData.length === 0) {
        return null; // Cannot calculate growth if previous year has no data
      }
      
      const yoyGrowthPct = ((currentYearTotal - previousYearTotal) / previousYearTotal) * 100;
      
      return {
        x: `${year}`,
        y: yoyGrowthPct
      };
    }).filter(item => item !== null && !isNaN(item.y)); // Filter out years with no data
    
    // Create a single line showing year-over-year growth percentages
    const yoyGrowthData = [{
      id: 'Year-over-Year Growth',
      color: '#3d71b8',
      data: yoyGrowthDataPoints
    }];
    
    // Clean percentage data - ensure all x values are valid strings
    const cleanPercentageData = (dataArray) => {
      return dataArray
        .filter(point => point && point.x != null && point.x !== '' && point.y != null && !isNaN(point.y))
        .map(point => ({
          x: String(point.x).trim(),
          y: Number(point.y) || 0
        }));
    };
    
    // Build the percentageData array
    const percentageDataArray = [];
    
    // Add current year data if it exists
    const cleanedCurrentYearData = cleanPercentageData(currentYearMonthlyGrowth);
    if (cleanedCurrentYearData.length > 0) {
      percentageDataArray.push({
        id: `${currentYear}`,
        color: '#3d71b8', // Blue color for current year
        data: cleanedCurrentYearData,
      });
    }
    
    // Only add previous year data if "All" is selected (for comparison)
    // When a specific year is selected, only show that year's data
    if (selectedYear === 'All') {
      const cleanedPreviousYearData = cleanPercentageData(previousYearMonthlyGrowth);
      if (cleanedPreviousYearData.length > 0) {
        percentageDataArray.push({
          id: `${previousYear}`,
          color: '#9ca3af', // Gray color for previous year
          data: cleanedPreviousYearData,
        });
      }
    }
    
    return {
      percentageData: percentageDataArray,
      usdData: yoyGrowthData.map(series => ({
        ...series,
        data: series.data
          .filter(point => point && point.x != null && point.x !== '' && point.y != null && !isNaN(point.y))
          .map(point => ({
            x: String(point.x).trim(),
            y: Number(point.y) || 0
          }))
      })).filter(series => series.data && series.data.length > 0)
    };
  }, [data?.monthlyData, selectedYear]);

  // Transform monthly data for Nivo area + line chart
  const monthlyAreaLineData = useMemo(() => {
    if (!data || !data.monthlyData || !Array.isArray(data.monthlyData)) return [];
    
    // Filter data based on selected year and validate dates - be very strict
    const filteredMonthlyData = data.monthlyData
      .filter(d => d && d.month && typeof d.month === 'string' && d.month.trim() !== '' && isValidDate(d.month))
      .filter(d => {
        if (selectedYear === 'All') return true;
        try {
          const itemYear = d.month.split('-')[0];
          return itemYear === selectedYear;
        } catch (e) {
          return false;
        }
      });
    
    // Helper to safely create data points
    const createDataPoint = (month, value) => {
      if (!month || typeof month !== 'string' || !isValidDate(month)) return null;
      return {
        x: month,
        y: value != null && !isNaN(value) ? Number(value) : 0,
      };
    };
    
    const monthlyValueData = filteredMonthlyData
      .map(d => createDataPoint(d.month, d.value))
      .filter(point => point !== null);
    
    const rolling3MData = filteredMonthlyData
      .map(d => createDataPoint(d.month, d.rolling3M))
      .filter(point => point !== null);
    
    const rolling12MData = filteredMonthlyData
      .map(d => createDataPoint(d.month, d.rolling12M))
      .filter(point => point !== null);
    
    return [
      {
        id: 'Monthly Value',
        color: (mineralColors && mineralColors[selectedMineral]) || '#3b82f6',
        data: monthlyValueData,
      },
      {
        id: 'Rolling 3M',
        color: '#10b981',
        data: rolling3MData,
      },
      {
        id: 'Rolling 12M',
        color: '#f59e0b',
        data: rolling12MData,
      },
    ].filter(series => series.data && series.data.length > 0);
  }, [data?.monthlyData, mineralColors, selectedMineral, selectedYear]);

  // Function to calculate year ranges based on selected year
  const getYearRanges = (selectedYear) => {
    if (selectedYear === 'All') {
      // If 'All' is selected, use current year as reference
      const currentYear = new Date().getFullYear();
      return {
        threeYear: `${currentYear - 2}-${currentYear}`,
        fiveYear: `${currentYear - 4}-${currentYear}`
      };
    }
    
    const year = parseInt(selectedYear);
    return {
      threeYear: `${year - 2}-${year}`,
      fiveYear: `${year - 4}-${year}`
    };
  };

  // Show loading state if data is not available
  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-700 rounded-lg transition-colors duration-200">
        <div className="text-center">
          <div className="text-2xl mb-2 animate-spin">🔄</div>
          <div className="text-gray-700 dark:text-gray-300">Loading time series data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Main Charts Section - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Value & Rolling Averages Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s – Trade Volume Momentum
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows monthly {selectedMineral} {selectedFlow}s values alongside 3-month and 12-month rolling averages. It helps visualize both short-term fluctuations and longer-term trends in import activity. It reveals hidden import momentum and smooths volatility, helping identify early supply or demand shifts. </p>
          <div style={{ height: 500 }}>
            {(() => {
              const cleanedData = deepCleanDataForNivo(monthlyAreaLineData);
              return cleanedData.length > 0 && cleanedData[0]?.data?.length > 0;
            })() ? (
              <ResponsiveLine
                data={deepCleanDataForNivo(monthlyAreaLineData)}
                margin={{ top: 20, right: 50, bottom: 120, left: 120 }}
                xScale={{
                  type: 'point',
                }}
                yScale={{
                  type: 'linear',
                  min: 0,
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  tickSize: 5,
                  tickPadding: 8,
                  tickRotation: -45,
                  legend: '',
                  legendOffset: 60,
                  format: (value) => {
                    // Format month labels
                    if (!value || value === null || value === undefined) return '';
                    try {
                      const date = new Date(value + '-01');
                      if (isNaN(date.getTime())) return value;
                      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                    } catch (e) {
                      return value;
                    }
                  }
                }}
                axisLeft={{
                  orient: 'left',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendOffset: -70,
                  legendPosition: 'middle',
                  format: formatCurrency
                }}
                colors={({ id }) => {
                  const colorMap = {
                    'Monthly Value': '#7c8db0',  // Subtle muted blue
                    'Rolling 3M': '#8bb3a0',    // Subtle teal
                    'Rolling 12M': '#d4a574'     // Subtle tan/salmon
                  };
                  return colorMap[id] || '#9ca3af';
                }}
                lineWidth={2}
                pointSize={4}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableArea={true}
                areaOpacity={0.3}
                areaBaselineValue={0}
                enableSlices={false}
                useMesh={true}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: 70,
                    itemsSpacing: 30,
                    itemDirection: 'left-to-right',
                    itemWidth: 140,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{point.serieId}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatMonthYear(point.data.x)}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatCurrency(point.data.y)}</div>
                  </div>
                )}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No monthly data available
              </div>
            )}
          </div>
        </div>

        {/* Top Countries Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s – Monthly Top Supplier Countries
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows the top three countries supplying {selectedMineral} each month and how much each contributed. It helps you see which countries dominated supply and how their share changed over time. It provides a clear view of monthly supplier concentration and sourcing shifts. Enables rapid assessment of geopolitical exposure and changing dependence on key trade partners.</p>
          <div style={{ height: 400 }}>
            {countryStackedBarData.data && countryStackedBarData.data.length > 0 ? (
              <ResponsiveBar
                data={countryStackedBarData.data}
                keys={countryStackedBarData.keys}
                indexBy="month"
                margin={{ top: 20, right: 110, bottom: 50, left: 120 }}
                padding={0.1}
                groupMode="stacked"
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={["#8bb3a0", "#d4a574", "#7c8db0"]}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: 32,
                  format: (value) => {
                    // Format month labels
                    if (!value || value === null || value === undefined) return '';
                    try {
                      const date = new Date(value + '-01');
                      if (isNaN(date.getTime())) return value;
                      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                    } catch (e) {
                      return value;
                    }
                  }
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendPosition: 'middle',
                  legendOffset: -70,
                  format: formatCurrency
                }}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 15,
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ id, value, indexValue }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{getCountryName(id)}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatMonthYear(indexValue)}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatCurrency(value)}</div>
                  </div>
                )}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#e5e7eb"
                label={d => formatCurrency(d.value)}
                labelFormat={d => formatCurrency(d)}
                role="application"
                ariaLabel="Top 3 countries stacked bar chart"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No country data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-Year and 5-Year Summary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s  Last 3 Years – ({getYearRanges(selectedYear).threeYear})
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows total {selectedMineral} {selectedFlow}s imports from each country over the past three years. It provides a long-term view of which countries have been the most important suppliers. It highlights how sourcing relationships evolve over time and supports strategic decisions on diversification, partner stability, and long-term geopolitical risk.</p>
          <div style={{ height: 300 }}>
            {threeYearBarData.length > 0 ? (
              <ResponsiveBar
                data={threeYearBarData}
                keys={['value']}
                indexBy="country"
                margin={{ top: 20, right: 30, bottom: 40, left: 120 }}
                padding={0.1}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#7c8db0', '#8bb3a0', '#d4a574', '#a892c7', '#9ca3af', '#b5a68c', '#9db5c7', '#c4a5a5']}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: 32
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendPosition: 'middle',
                  legendOffset: -70,
                  format: formatCurrency
                }}
                tooltip={({ indexValue, value, data }) => (
                   <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{data.fullName}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatCurrency(value)}</div>
                  </div>
                )}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#e5e7eb"
                label={d => formatCurrency(d.value)}
                labelFormat={d => formatCurrency(d)}
                role="application"
                ariaLabel="3-year summary bar chart"
                barAriaLabel={function(e){return e.id+": "+e.formattedValue+" in country: "+e.indexValue}}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No 3-year summary data available
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {selectedMineral} {selectedFlow}s – Last 5 Years ({getYearRanges(selectedYear).fiveYear})
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">
          This chart shows total {selectedMineral} {selectedFlow}s imports from each country over the past five years. It provides a long-term view of which countries have been the most important suppliers. It offers long-term visibility into import patterns, helping assess structural trends and plan for future procurement resilience.</p>
          <div style={{ height: 300 }}>
            {fiveYearBarData.length > 0 ? (
              <ResponsiveBar
                data={fiveYearBarData}
                keys={['value']}
                indexBy="country"
                margin={{ top: 20, right: 30, bottom: 40, left: 120 }}
                padding={0.1}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#7c8db0', '#8bb3a0', '#d4a574', '#a892c7', '#9ca3af', '#b5a68c', '#9db5c7', '#c4a5a5']}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: 32
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendPosition: 'middle',
                  legendOffset: -70,
                  format: formatCurrency
                }}
                tooltip={({ indexValue, value, data }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{data.fullName}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatCurrency(value)}</div>
                  </div>
                )}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#e5e7eb"
                label={d => formatCurrency(d.value)}
                labelFormat={d => formatCurrency(d)}
                role="application"
                ariaLabel="5-year summary bar chart"
                barAriaLabel={function(e){return e.id+": "+e.formattedValue+" in country: "+e.indexValue}}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No 5-year summary data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Growth Analysis and Moving Averages Charts - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Analysis Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {selectedMineral} {selectedFlow}s – Previous 3-Year vs 5-Year Periods Growth (USD)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart compares total {selectedMineral} {selectedFlow} in the most recent 3-year and 5-year periods with the corresponding previous periods. It helps show how {selectedFlow} volumes change over different time frames. Useful for understanding whether current market conditions represent a shift or continuation.</p>
          <div style={{ height: 300 }}>
            {growthAnalysisBarData.length > 0 ? (
              <ResponsiveBar
                data={growthAnalysisBarData}
                keys={['current', 'previous']}
                indexBy="period"
                margin={{ top: 20, right: 110, bottom: 80, left: 80 }}
                padding={0.1}
                groupMode="grouped"
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#7c8db0', '#9ca3af']}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: 32
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendPosition: 'middle',
                  legendOffset: -50,
                  format: formatCurrency
                }}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 12,
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ id, value, indexValue, data }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{indexValue}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">
                      {id === 'current' ? 'Current Period' : 'Previous Period'}: {formatCurrency(value)}
                    </div>
                    {id === 'current' && data.currentLabel && (
                      <div className="text-gray-600 dark:text-gray-400 text-xs">{data.currentLabel}</div>
                    )}
                    {id === 'previous' && data.previousLabel && (
                      <div className="text-gray-600 dark:text-gray-400 text-xs">{data.previousLabel}</div>
                    )}
                  </div>
                )}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#e5e7eb"
                label={d => formatCurrency(d.value)}
                labelFormat={d => formatCurrency(d)}
                role="application"
                ariaLabel="Growth analysis grouped bar chart"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No growth analysis data available
              </div>
            )}
          </div>
        </div>

        {/* Moving Averages Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s – Rolling Trend Indicators (USD)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart displays 3-month, 6-month, and 12-month rolling averages of {selectedMineral} import values. These trend lines smooth out month-to-month fluctuations, helping identify short-, medium-, and long-term patterns in trade activity. It shows trade stability or instability over time. Short-, mid-, and long-term smoothing helps detect early supply chain risks.</p>
          <div style={{ height: 300 }}>
            {(() => {
              const cleanedData = deepCleanDataForNivo(movingAveragesLineData);
              return cleanedData.length > 0 && cleanedData[0]?.data?.length > 0;
            })() ? (
              <ResponsiveLine
                data={deepCleanDataForNivo(movingAveragesLineData)}
                margin={{ top: 20, right: 110, bottom: 70, left: 120 }}
                xScale={{
                  type: 'point',
                }}
                yScale={{
                  type: 'linear',
                  min: 0,
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: '',
                  legendOffset: 50,
                  tickValues: (() => {
                    const cleaned = deepCleanDataForNivo(movingAveragesLineData);
                    const dataLength = cleaned[0]?.data?.length || 0;
                    return dataLength > 24 ? 'every 6 months' : dataLength > 12 ? 'every 3 months' : 'every month';
                  })(),
                  format: (value) => {
                    // Format month labels
                    if (!value || value === null || value === undefined) return '';
                    try {
                      const date = new Date(value + '-01');
                      if (isNaN(date.getTime())) return value;
                      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                    } catch (e) {
                      return value;
                    }
                  }
                }}
                axisLeft={{
                  orient: 'left',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'USD',
                  legendOffset: -70,
                  legendPosition: 'middle',
                  tickValues: 5,
                  format: formatCurrency
                }}
                colors={({ id }) => {
                  const colorMap = {
                    '3-Month': '#8bb3a0',    // Subtle teal
                    '6-Month': '#d4a574',     // Subtle tan/salmon
                    '12-Month': '#a892c7'     // Subtle lavender/purple
                  };
                  return colorMap[id] || '#9ca3af';
                }}
                pointSize={4}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 0,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{point.serieId}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatMonthYear(point.data.x)}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">{formatCurrency(point.data.y)}</div>
                  </div>
                )}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No moving averages data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Year-over-Year Growth Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* YoY Growth Percentage Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s – Monthly Growth Rate (%)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart tracks short-term growth trends in the trade of critical minerals. 
          It provides a quick view of performance changes and helps detect emerging shifts in demand, pricing, or supply conditions. </p>
          <div style={{ height: 300 }}>
            {(() => {
              if (!yoyGrowthLineData || !yoyGrowthLineData.percentageData) return false;
              const cleanedData = deepCleanDataForNivo(yoyGrowthLineData.percentageData);
              return cleanedData.length > 0 && cleanedData[0]?.data?.length > 0;
            })() ? (
              <ResponsiveLine
                data={deepCleanDataForNivo(yoyGrowthLineData.percentageData || [])}
                margin={{ top: 20, right: 30, bottom: 50, left: 120 }}
                xScale={{
                  type: 'point',
                }}
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: '',
                  legendOffset: 36,
                  format: (value) => {
                    // Format month labels (Jan, Feb, Mar, etc.)
                    return value;
                  }
                }}
                axisLeft={{
                  orient: 'left',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: '%',
                  legendOffset: -70,
                  legendPosition: 'middle',
                  format: (value) => `${value.toFixed(2)}%`
                }}
                colors={({ id }) => {
                  const currentYear = selectedYear === 'All' ? new Date().getFullYear() : parseInt(selectedYear);
                  const previousYear = currentYear - 1;
                  const colorMap = {
                    [currentYear]: '#3d71b8',  // Muted Blue
                    [previousYear]: '#9ca3af'   // Gray
                  };
                  return colorMap[id] || '#9ca3af';
                }}
                lineWidth={2}
                pointSize={4}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableSlices={false}
                useMesh={true}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                markers={[
                  {
                    axis: 'y',
                    value: 0,
                    lineStyle: { stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '2,2' },
                  }
                ]}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: 50,
                    itemsSpacing: 20,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{point.serieId}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">Month: {point.data.x}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">Growth Rate: {point.data.y.toFixed(2)}%</div>
                  </div>
                )}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No monthly growth rate data available. At least two months of data are required to calculate growth rates.
              </div>
            )}
          </div>
        </div>

        {/* YoY Growth Percentage Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} {selectedFlow}s – Year-over-Year Growth Trend (%)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart compares each year's total to the previous year's total. It provides a clear view of annual growth patterns over time and shows long-term structural trend direction — critical for understanding if the {selectedMineral} market is expanding or contracting year to year.</p>
          <div style={{ height: 300 }}>
            {(() => {
              if (!yoyGrowthLineData || !yoyGrowthLineData.usdData) return false;
              const cleanedData = deepCleanDataForNivo(yoyGrowthLineData.usdData);
              return cleanedData.length > 0 && cleanedData[0]?.data?.length > 0;
            })() ? (
              <ResponsiveLine
                data={deepCleanDataForNivo(yoyGrowthLineData.usdData || [])}
                margin={{ top: 20, right: 30, bottom: 100, left: 120 }}
                xScale={{
                  type: 'point',
                  sort: (a, b) => parseInt(a) - parseInt(b)
                }}
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Year',
                  legendOffset: 36,
                  format: (value) => {
                    // Format year labels
                    return value;
                  }
                }}
                axisLeft={{
                  orient: 'left',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: '%',
                  legendOffset: -70,
                  legendPosition: 'middle',
                  format: (value) => `${value.toFixed(1)}%`
                }}
                colors={({ id }) => {
                  // Use blue color for the year-over-year growth line
                  return '#3d71b8';
                }}
                lineWidth={2}
                pointSize={6}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableSlices={false}
                useMesh={true}
                theme={nivoTheme}
                animate={true}
                motionConfig="gentle"
                markers={[
                  {
                    axis: 'y',
                    value: 0,
                    lineStyle: { stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '2,2' },
                  }
                ]}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: 60,
                    itemsSpacing: 10,
                    itemDirection: 'left-to-right',
                    itemWidth: 120,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200 max-w-xs">
                    <div className="text-gray-900 dark:text-white font-semibold text-sm">{point.serieId}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">Year: {point.data.x}</div>
                    <div className="text-gray-700 dark:text-gray-300 text-sm">YoY Growth: {point.data.y.toFixed(2)}%</div>
                  </div>
                )}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                No YoY growth percentage data available
              </div>
            )}
          </div>
        </div>


      </div>

      {/* Summary Statistics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Summary Statistics - {selectedMineral}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(() => {
            // Filter data based on selected year (same logic as monthlyAreaLineData)
            const filteredMonthlyData = data?.monthlyData?.filter(d => {
              if (selectedYear === 'All') return true;
              const itemYear = d.month.split('-')[0];
              return itemYear === selectedYear;
            }) || [];
            
            // Calculate summary statistics from the filtered data
            const latestMonth = filteredMonthlyData.slice(-1)[0];
            const previousMonth = filteredMonthlyData.slice(-2, -1)[0];
            
            // Calculate month-over-month growth
            const momGrowth = latestMonth && previousMonth ? 
              ((latestMonth.value - previousMonth.value) / previousMonth.value * 100) : null;
            
            // Calculate year-over-year growth by comparing total values for current vs previous year
            let yoyGrowth = null;
            if (filteredMonthlyData.length > 0) {
              const currentYear = selectedYear === 'All' ? new Date().getFullYear() : parseInt(selectedYear);
              const previousYear = currentYear - 1;
              
              // Get all data for current year
              const currentYearData = data?.monthlyData?.filter(d => {
                const itemYear = parseInt(d.month.split('-')[0]);
                return itemYear === currentYear;
              }) || [];
              
              // Get all data for previous year
              const previousYearData = data?.monthlyData?.filter(d => {
                const itemYear = parseInt(d.month.split('-')[0]);
                return itemYear === previousYear;
              }) || [];
              
              // Calculate total values for each year
              const currentYearTotal = currentYearData.reduce((sum, d) => sum + (d.value || 0), 0);
              const previousYearTotal = previousYearData.reduce((sum, d) => sum + (d.value || 0), 0);
              
              // Calculate YoY growth if we have data for both years
              if (currentYearTotal > 0 && previousYearTotal > 0) {
                yoyGrowth = ((currentYearTotal - previousYearTotal) / previousYearTotal * 100);
              }
            }
            
            return (
              <>
                {/* Latest Month */}
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                  <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-2">
                    Last Month ({latestMonth?.month ? latestMonth.month.split('-').reverse().join('-') : 'N/A'})
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 font-semibold text-xl">
                    {latestMonth ? formatCurrency(latestMonth.value) : 'N/A'}
                  </div>
                </div>

                {/* Month-over-Month Growth */}
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                  <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-2">
                    Month-over-Month Growth
                  </div>
                  <div className={`font-semibold text-xl ${momGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {momGrowth !== null ? `${momGrowth >= 0 ? '+' : ''}${momGrowth.toFixed(1)}%` : 'N/A'}
                  </div>
                </div>

                {/* Year-over-Year Growth */}
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                  <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-2">
                    Year-over-Year Growth
                  </div>
                  <div className={`font-semibold text-xl ${yoyGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {yoyGrowth !== null ? `${yoyGrowth >= 0 ? '+' : ''}${yoyGrowth.toFixed(1)}%` : 'N/A'}
                  </div>
                </div>

                {/* Latest Value */}
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                  <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-2">
                    Latest Traded Value
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 font-semibold text-xl">
                    {latestMonth ? formatCurrency(latestMonth.value) : 'N/A'}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default TimeSeriesCharts;
